import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Trans } from "react-i18next";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { track } from "@/lib/track";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";

const BOOK_CALL_URL = "https://www.cal.eu/andreacab/holymoly-free-call?user=andreacab";

/**
 * Cycles through target words with a slide-up entrance. SSR renders words[0],
 * so the prerendered HTML matches the first client frame (hydration-safe).
 * All words are stacked invisibly in one grid cell so the slot is always as
 * wide as the longest word — the surrounding line never reflows on swap.
 */
export function RotatingWord({ words }: { words: string[] }) {
  const [index, setIndex] = React.useState(0);
  React.useEffect(() => {
    if (words.length < 2) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % words.length);
    }, 2600);
    return () => clearInterval(id);
  }, [words.length]);
  return (
    <span className="inline-grid text-left align-baseline">
      {words.map((w) => (
        <span key={w} aria-hidden="true" className="invisible col-start-1 row-start-1">
          {w}
        </span>
      ))}
      <span key={index} className="animate-word-in col-start-1 row-start-1">
        {words[index]}
      </span>
    </span>
  );
}

interface CtaBandProps {
  /** i18n key for the title; may contain <italic> markers. */
  titleKey?: string;
  /** Rotating-title variant: static lead text + cycling accent words. */
  titleLead?: string;
  rotateWords?: string[];
  /** Primary pill: opens the booking link in a new tab. */
  bookLabel: string;
  /** Secondary pill: internal route. */
  secondaryLabel: string;
  secondaryTo: string;
  /** Ghost link: internal route. */
  ghostLabel: string;
  ghostTo: string;
  /** Analytics source tag. */
  trackSource: string;
}

/**
 * Dark closing band for standalone marketing pages (/about, /process) —
 * a compact sibling of the landing FinalCTA, sharing its visual language.
 */
export function CtaBand({
  titleKey,
  titleLead,
  rotateWords,
  bookLabel,
  secondaryLabel,
  secondaryTo,
  ghostLabel,
  ghostTo,
  trackSource,
}: CtaBandProps) {
  const localizedHref = useLocalizedHref();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="frame relative overflow-hidden bg-ink text-page-bg">
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(239,77,35,0.18), transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-20 -left-20 h-[28rem] w-[28rem] rounded-full bg-accent/5 blur-3xl"
      />
      <div className="container-narrow relative py-20 text-center lg:py-28">
        <h2
          className={cn(
            "mx-auto max-w-[22ch] text-[clamp(32px,4.6vw,56px)] leading-[1.06] font-medium tracking-[-0.03em]",
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          {titleLead && rotateWords?.length ? (
            <>
              {titleLead}{" "}
              <span className="serif-italic whitespace-nowrap text-accent">
                <RotatingWord words={rotateWords} />
              </span>
            </>
          ) : (
            <Trans
              i18nKey={titleKey}
              components={{
                italic: <span className="serif-italic text-accent" />,
              }}
            />
          )}
        </h2>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <a
            href={BOOK_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("cta_click", { source: trackSource })}
            className="inline-flex items-center gap-2 rounded-full bg-page-bg py-2 pr-2 pl-5 text-[14px] font-medium tracking-tight text-ink transition-colors hover:bg-white"
          >
            {bookLabel}
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-ink/10">
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
          </a>
          <Link
            to={localizedHref(secondaryTo)}
            className="inline-flex items-center gap-1.5 rounded-full border border-page-bg/30 bg-page-bg/10 px-4 py-2 text-[14px] font-medium tracking-tight text-page-bg backdrop-blur transition-colors hover:bg-page-bg/15"
          >
            {secondaryLabel}
          </Link>
          <Link
            to={localizedHref(ghostTo)}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[14px] font-medium tracking-tight text-page-bg/80 transition-colors hover:bg-page-bg/5 hover:text-page-bg"
          >
            {ghostLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}
